import { useState } from 'react';
import { Box, Button, Card, CardContent, Divider, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import StatusBadge, { COC_STATUS_MAP } from '../../../components/shared/StatusBadge';

const COCRequestList = ({ cocRequests, onViewRequest }) => {
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredRequests = filterStatus === 'all'
    ? cocRequests
    : cocRequests.filter(req => req.status === filterStatus);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>COC Request History</Typography>
          <Typography variant="caption" color="text.secondary">{filteredRequests.length} {filteredRequests.length === 1 ? 'request' : 'requests'}</Typography>
        </Box>
        <TextField select size="small" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} sx={{ width: 180 }}>
          <MenuItem value="all">All Statuses</MenuItem>
          <MenuItem value="Submitted">Submitted</MenuItem>
          <MenuItem value="Under Review">Under Review</MenuItem>
          <MenuItem value="Approved">Approved</MenuItem>
          <MenuItem value="Rejected">Rejected</MenuItem>
          <MenuItem value="More Info Required">More Info Required</MenuItem>
        </TextField>
      </Stack>

      {filteredRequests.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <DescriptionIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">No COC requests found</Typography>
          <Typography variant="body2" color="text.secondary">{filterStatus === 'all' ? 'No Change of Circumstance requests have been submitted for this loan.' : `No requests with status "${filterStatus}".`}</Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {filteredRequests.map(request => (
            <Card variant="outlined" key={request.id}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2">{request.categoryName}</Typography>
                      <StatusBadge status={request.status} map={COC_STATUS_MAP} />
                      {request.revisedDisclosureIssued && <StatusBadge variant="teal">Revised LE Issued</StatusBadge>}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{request.description.substring(0, 150)}{request.description.length > 150 ? '...' : ''}</Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6, md: 3 }}><Typography variant="caption" color="text.secondary">Submitted</Typography><Typography variant="body2" fontWeight={700}>{formatDate(request.submittedDate)}</Typography></Grid>
                      <Grid size={{ xs: 6, md: 3 }}><Typography variant="caption" color="text.secondary">Submitted By</Typography><Typography variant="body2" fontWeight={700}>{request.submittedBy}</Typography></Grid>
                      <Grid size={{ xs: 6, md: 3 }}><Typography variant="caption" color="text.secondary">Discovery Date</Typography><Typography variant="body2" fontWeight={700}>{new Date(request.discoveryDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</Typography></Grid>
                      <Grid size={{ xs: 6, md: 3 }}><Typography variant="caption" color="text.secondary">Impacted Elements</Typography><Typography variant="body2" fontWeight={700}>{request.impactedElements.length}</Typography></Grid>
                    </Grid>
                  </Box>
                  <Button variant="contained" size="small" onClick={() => onViewRequest(request)}>View Details</Button>
                </Stack>
                {request.reviewedBy && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6, md: 3 }}><Typography variant="caption" color="text.secondary">Reviewed By</Typography><Typography variant="body2" fontWeight={700}>{request.reviewedBy}</Typography></Grid>
                      <Grid size={{ xs: 6, md: 3 }}><Typography variant="caption" color="text.secondary">Reviewed Date</Typography><Typography variant="body2" fontWeight={700}>{formatDate(request.reviewedDate)}</Typography></Grid>
                      <Grid size={{ xs: 12, md: 6 }}><Typography variant="caption" color="text.secondary">Review Notes</Typography><Typography variant="body2" fontWeight={700}>{request.reviewNotes || 'No notes provided'}</Typography></Grid>
                    </Grid>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default COCRequestList;
